import { tt } from '@agos/shared'

import type { RoleDelegationEdge, RolePerson, RoleView } from '../roles/roles-api'
import { membersOf, orderRoles, rightsCountText, roleNameOf } from './role-model'

/**
 * *Kto spravuje role* - the delegation graph as the main administrator draws it, `FRONTEND.md` §9.752 on
 * `BACKEND.md` §10A.408.
 *
 * **Delegation is structure, not a permission key**: one edge per (manager role → target role) with two verbs,
 * *upravovať práva* (`edit`) and *prideľovať ľuďom* (`assign`), written whole by `PUT /settings/roles/delegations`.
 * The server checks the whole graph (R5) and refuses with `ROLE_CYCLE` and friends; these rules say the same thing
 * **before** the press, so the picker can grey out a target with its reason rather than let somebody draw an edge
 * the save will refuse.
 */

export type Verb = 'assign' | 'edit'

/** One pair's identity - a manager manages a target at most once (`staff_role_delegations_pair_unique`). */
export function pairKey(manager: string, target: string): string {
  return `${manager}>${target}`
}

function sortEdges(edges: readonly RoleDelegationEdge[]): RoleDelegationEdge[] {
  return [...edges].sort(
    (left, right) =>
      left.manager.localeCompare(right.manager) || left.target.localeCompare(right.target),
  )
}

/**
 * The graph as the overview answers it, from both ends.
 *
 * Each role lists what it `manages` and what it is `managedBy`; the main administrator's overview carries every role,
 * so either list alone holds every edge. **Read from both**, because an edge whose manager role is not in the list
 * read would otherwise vanish from the tab and then from the save - which replaces the graph whole, so dropping an
 * edge from the screen is deleting it. An edge with neither verb is not an edge (V89's `does_something`).
 */
export function edgesOf(roles: readonly RoleView[]): RoleDelegationEdge[] {
  const found = new Map<string, RoleDelegationEdge>()
  for (const role of roles) {
    for (const edge of role.manages) {
      if (!edge.assign && !edge.edit) continue
      found.set(pairKey(role.key, edge.role), {
        manager: role.key,
        target: edge.role,
        assign: edge.assign,
        edit: edge.edit,
      })
    }
  }
  for (const role of roles) {
    for (const edge of role.managedBy) {
      if (!edge.assign && !edge.edit) continue
      const key = pairKey(edge.role, role.key)
      if (found.has(key)) continue
      found.set(key, { manager: edge.role, target: role.key, assign: edge.assign, edit: edge.edit })
    }
  }
  return sortEdges([...found.values()])
}

/**
 * Whether `from` manages `to` through any chain of edges - the cycle rule's question.
 *
 * Breadth-first over the managers' edges, with every role visited once, so a graph the server would refuse (a cycle
 * already drawn) still answers rather than looping.
 */
export function reaches(edges: readonly RoleDelegationEdge[], from: string, to: string): boolean {
  const seen = new Set<string>([from])
  const queue = [from]
  while (queue.length > 0) {
    const current = queue.shift() as string
    for (const edge of edges) {
      if (edge.manager !== current || (!edge.assign && !edge.edit)) continue
      if (edge.target === to) return true
      if (!seen.has(edge.target)) {
        seen.add(edge.target)
        queue.push(edge.target)
      }
    }
  }
  return false
}

/** Why a role may not be managed by another - the picker's greyed-out reasons. */
export type TargetProblem = 'self' | 'main-admin' | 'admin-base' | 'cycle' | 'unknown'

/**
 * The reason `manager` may not manage `target`, or null - §10A.408's R5, in the order the server asks it.
 *
 * - **its own role** - nobody edits or assigns their own role (R6);
 * - **the main administrator** - it has no `staff_roles` row, so the foreign key refuses it before any rule does;
 * - **a role based on Administrátor**, the built-in one included: such a role passes 235 role-list calls whatever its
 *   ticks, so handing it out is making administrators, which O113 keeps with the main administrator;
 * - **a cycle**: the target already manages the manager, directly or through others, so the edge would close a loop.
 *
 * A role the reader cannot see answers `unknown` rather than null - an edge to something unread is not an edge the
 * screen can vouch for.
 */
export function targetProblem(
  edges: readonly RoleDelegationEdge[],
  manager: string,
  target: string,
  roles: readonly RoleView[],
): TargetProblem | null {
  if (target === manager) return 'self'
  if (target === 'main_admin' || manager === 'main_admin') return 'main-admin'
  const role = roles.find((candidate) => candidate.key === target)
  if (role === undefined) return 'unknown'
  if (role.baseRole === 'admin') return 'admin-base'
  const others = edges.filter((edge) => !(edge.manager === manager && edge.target === target))
  if (reaches(others, target, manager)) return 'cycle'
  return null
}

/** *vlastná rola · základ Administrátor · spravuje ju* - the design's three, and the two the server adds. */
export function problemText(problem: TargetProblem): string {
  switch (problem) {
    case 'self':
      return tt('vlastná rola')
    case 'main-admin':
      return tt('hlavný administrátor')
    case 'admin-base':
      return tt('základ Administrátor')
    case 'cycle':
      return tt('spravuje ju')
    case 'unknown':
      return tt('neznáma rola')
  }
}

/** Every edge the whole graph would be refused for - the save waits until this is empty. */
export function graphProblems(
  edges: readonly RoleDelegationEdge[],
  roles: readonly RoleView[],
): { edge: RoleDelegationEdge; problem: TargetProblem }[] {
  const found: { edge: RoleDelegationEdge; problem: TargetProblem }[] = []
  for (const edge of edges) {
    const problem = targetProblem(edges, edge.manager, edge.target, roles)
    if (problem !== null) found.push({ edge, problem })
  }
  return found
}

/**
 * The graph with one verb of one pair switched - and **an edge left with neither verb is dropped**, because the server
 * refuses to store one (V89's `staff_role_delegations_does_something`).
 */
export function withVerb(
  edges: readonly RoleDelegationEdge[],
  manager: string,
  target: string,
  verb: Verb,
  enabled: boolean,
): RoleDelegationEdge[] {
  const current = edges.find((edge) => edge.manager === manager && edge.target === target) ?? {
    manager,
    target,
    assign: false,
    edit: false,
  }
  const rest = edges.filter((edge) => !(edge.manager === manager && edge.target === target))
  const next = { ...current, [verb]: enabled }
  return sortEdges(next.assign || next.edit ? [...rest, next] : rest)
}

/** Every edge of one manager role dropped - the *×* on a manager's sentence row. */
export function withoutManager(
  edges: readonly RoleDelegationEdge[],
  manager: string,
): RoleDelegationEdge[] {
  return edges.filter((edge) => edge.manager !== manager)
}

export interface DelegationDiff {
  added: RoleDelegationEdge[]
  removed: RoleDelegationEdge[]
  changed: { before: RoleDelegationEdge; after: RoleDelegationEdge }[]
}

export function delegationDiff(
  before: readonly RoleDelegationEdge[],
  after: readonly RoleDelegationEdge[],
): DelegationDiff {
  const was = new Map(before.map((edge) => [pairKey(edge.manager, edge.target), edge]))
  const now = new Map(after.map((edge) => [pairKey(edge.manager, edge.target), edge]))
  const diff: DelegationDiff = { added: [], removed: [], changed: [] }
  for (const [key, edge] of now) {
    const old = was.get(key)
    if (old === undefined) diff.added.push(edge)
    else if (old.assign !== edge.assign || old.edit !== edge.edit) diff.changed.push({ before: old, after: edge })
  }
  for (const [key, edge] of was) {
    if (!now.has(key)) diff.removed.push(edge)
  }
  return diff
}

export function delegationIsDirty(diff: DelegationDiff): boolean {
  return diff.added.length + diff.removed.length + diff.changed.length > 0
}

/**
 * The one-click starter, *Administrátori spravujú Kuchára, Čašníka a Šoféra* - owner question #166, answered by the
 * design's recommendation: offered on the screen, never seeded by a migration, so nothing changes until it is pressed
 * **and saved**.
 */
export const STARTER_MANAGER = 'admin'
export const STARTER_TARGETS: readonly string[] = ['cook', 'waiter', 'driver']

/** The graph with the starter's three edges, both verbs, over whatever was drawn - only for roles that exist. */
export function withStarter(
  edges: readonly RoleDelegationEdge[],
  roles: readonly RoleView[],
): RoleDelegationEdge[] {
  if (!roles.some((role) => role.key === STARTER_MANAGER)) return [...edges]
  let next = [...edges]
  for (const target of STARTER_TARGETS) {
    if (!roles.some((role) => role.key === target)) continue
    next = withVerb(next, STARTER_MANAGER, target, 'edit', true)
    next = withVerb(next, STARTER_MANAGER, target, 'assign', true)
  }
  return next
}

/** Whether the starter has nothing left to add - the button is not offered then. */
export function starterApplied(edges: readonly RoleDelegationEdge[], roles: readonly RoleView[]): boolean {
  return withStarter(edges, roles).every((edge) =>
    edges.some(
      (drawn) =>
        drawn.manager === edge.manager &&
        drawn.target === edge.target &&
        drawn.assign === edge.assign &&
        drawn.edit === edge.edit,
    ),
  )
}

/**
 * The rights an assign edge passes on: what the target role holds and the manager role does not.
 *
 * **Literal, as the server's grantability is** (design §2.1 decision 9, *family-aware grantability* not built): the
 * main administrator drew the edge knowing what the role holds, so the server lets the assignment through - and the
 * screen says what that gives away. **The built-in Administrátor passes on nothing**: it holds every right the server
 * checks (#58), so no right of the target is beyond it.
 */
export function rightsPassedOn(
  manager: Pick<RoleView, 'permissions' | 'administratorPass'> | undefined,
  target: Pick<RoleView, 'permissions'> | undefined,
  catalogue: readonly string[],
): string[] {
  if (manager === undefined || target === undefined || manager.administratorPass) return []
  const held = new Set(manager.permissions)
  const known = new Set(catalogue)
  return [...new Set(target.permissions)].filter((key) => known.has(key) && !held.has(key))
}

/** *Kuchár má 3 práva, ktoré Vedúci zmeny nemá - kto ju prideľuje, dá ich ďalej.* */
export function passedOnText(
  managerName: string,
  targetName: string,
  count: number,
): string {
  return `${targetName} ${tt('má')} ${rightsCountText(count)}, ${tt('ktoré')} ${managerName} ${tt('nemá - kto ju prideľuje, dá ich ďalej.')}`
}

/**
 * Who a save hands power to: the members of every manager role that gains an edge or a verb - the confirmation names
 * them, because the people are what the main administrator is really deciding about.
 */
export function gainers(
  diff: DelegationDiff,
  roles: readonly RoleView[],
  people: readonly RolePerson[],
): { manager: string; name: string; people: RolePerson[] }[] {
  const managers = new Set<string>()
  for (const edge of diff.added) managers.add(edge.manager)
  for (const { before, after } of diff.changed) {
    if ((after.assign && !before.assign) || (after.edit && !before.edit)) managers.add(after.manager)
  }
  return orderRoles(roles)
    .filter((role) => managers.has(role.key))
    .map((role) => ({ manager: role.key, name: roleNameOf(roles, role.key), people: membersOf(role.key, people) }))
}

/** The four rules, always visible on the tab - design §2.9. */
export function delegationRules(): string[] {
  return [
    tt('Nikto neupravuje ani neprideľuje vlastnú rolu.'),
    tt('Pridať možno len práva, ktoré má ten, kto rolu spravuje.'),
    tt('Hlavného administrátora a roly so základom Administrátor spravuje len hlavný administrátor.'),
    tt('Žiadna rola nespravuje rolu, ktorá spravuje ju.'),
  ]
}
